import { useState, useEffect, useRef, type FC } from "react";

import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { FieldError } from "@/shared/components/ui/FieldError";
import { OtpInput } from "@/shared/components/ui/OtpInput";
import { SecurityNote } from "@/shared/components/ui/security-note";

interface VerifyEmailStepProps {
  email: string;
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<boolean>;
  onChangeEmail: () => void;
  isLoading: boolean;
  error: string | null;
}

export const VerifyEmailStep: FC<VerifyEmailStepProps> = ({
  email,
  onVerify,
  onResend,
  onChangeEmail,
  isLoading,
  error,
}) => {
  const [otp, setOtp] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const submittedRef = useRef(false);

  useEffect(() => {
    if (otp.length === 6 && !isLoading && !submittedRef.current) {
      submittedRef.current = true;
      void onVerify(otp);
    }
    if (otp.length < 6) {
      submittedRef.current = false;
    }
  }, [otp, isLoading, onVerify]);

  useEffect(() => {
    if (resendCooldown <= 0) return;

    const interval = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCooldown]);

  const handleResend = async () => {
    const success = await onResend();
    if (success) {
      setResendCooldown(30);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <h1>Check your inbox</h1>

      <p className="text-center text-secondary">
        We sent a 6-digit code to{" "}
        <span className="break-words font-medium text-primary">{email}</span>{" "}
        <button
          type="button"
          onClick={onChangeEmail}
          className="whitespace-nowrap font-medium text-brand underline"
        >
          Change email
        </button>
      </p>

      <OtpInput length={6} value={otp} onChange={setOtp} error={!!error} disabled={isLoading} />

      <FieldError message={error} />

      <p className="text-center text-sm text-secondary">
        Didn't get it? Check your spam folder or{" "}
        <Button
          variant="link"
          size={null}
          type="button"
          onClick={handleResend}
          disabled={isLoading || resendCooldown > 0}
          className="inline text-sm text-brand underline"
        >
          {resendCooldown > 0 ? `resend code (${resendCooldown}s)` : "resend code"}
        </Button>
      </p>

      <Button
        type="button"
        variant="brand"
        className="w-full"
        onClick={() => onVerify(otp)}
        disabled={isLoading || otp.length < 6}
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <>
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>

      <SecurityNote text="Bank-level encryption, and we never sell your information" />
    </div>
  );
};
